import React, { useMemo, useContext } from 'react';
import Link from 'next/link';
import { WebImage } from './styled';
import ModalPortal from './library/portal/ModalPortal';
import AnimatedElement from './library/AnimatedElement/AnimatedElement';
import TooltipElement from './library/TooltipElement';
import AlertElement from './library/AlertElement/AlertElement';
import SvgSpriteWeb from './library/SvgSprite';
import FieldErrorElement from './library/FieldErrorElement/FieldErrorElement';
import FieldInfoElement from './library/FieldInfoElement/FieldInfoElement';
import SliderComponent from '../widgets/slider/SliderComponent';
import project from '../scripts/configs/project';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { getLang } from '../common/utils/router';
import UserContext from '../common/utils/getContext';

export const zendoStorage = {
    get: (item) => {
        if (typeof window != 'undefined') {
            try {
                return window?.sessionStorage?.getItem(item) || window?.localStorage?.getItem(item);
            } catch (e) {
                console.error(e);
            }
        }
        return null;
    },
    set: (item, val, session) => {
        if (typeof window != 'undefined') {
            try {
                session ? window?.sessionStorage?.setItem(item, val) : window?.localStorage?.setItem(item, val);
            } catch (e) {
                console.error(e);
            }
        }
    },
    remove: (item) => {
        window?.localStorage?.removeItem(item);
        window?.sessionStorage?.removeItem(item);
    }
};

export const svgSprite = (name, dataStyles, id, stroke = []) => {
    const [strokeDasharray, strokeDashoffset] = stroke;
    let newDataStyles = {
        ...dataStyles,
        className: dataStyles?.className || dataStyles?.style?.className
    };
    if (newDataStyles.style && typeof newDataStyles.style !== 'object') {
        console.error(`Check icons style attribute ${name}`);
        newDataStyles.style = {};
    }
    return (
        <SvgSpriteWeb
            name={name}
            id={id}
            dataStyles={{
                width: '16px',
                ...newDataStyles,
                ...{ height: newDataStyles.height === false ? undefined : newDataStyles.height || '16px' }
            }}
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
        />
    );
};

export const A = (innerProps) => {
    const props = {
        ...innerProps,
        ref: innerProps.childrenRef,
        childrenref: undefined
    };

    const { lang, routerReLoader, routerLoader } = useContext(UserContext);
    const router = useRouter();
    const { download, href = '/', rel = 'nofollow noreferrer' } = props;

    const routeLang = getLang(router.asPath);
    const paramsData = useMemo(() => {
        if (href && typeof href !== 'string') {
            const { as, pathname, query } = href;
            return {
                href: {
                    pathname: `/[lang]` + pathname,
                    query: { ...query, lang: routeLang === lang ? lang : 'default' }
                },
                as: `${routeLang === lang ? '/' + lang : ''}${as}`
            };
        } else if (href?.[0] === '/') {
            if (routeLang) {
                return {
                    href: `${routeLang === lang ? '/' + lang : ''}${href}`
                };
            }
            return {
                href: {
                    pathname: `/[lang]` + href,
                    query: { lang: routeLang === lang ? lang : 'default' }
                },
                as: href
            };
        }
        return false;
    }, [routeLang, lang, href]);
    const onClick = useMemo(() => {
        if (!props?.onClick && paramsData && (paramsData?.as === router.asPath || paramsData?.href === router.asPath)) {
            return (e) => {
                e.preventDefault();
                router.reload();
            };
        }
    }, [paramsData, props?.onClick, router]);

    if (paramsData && !download) {
        if (onClick || routerReLoader) {
            return (
                <a
                    {...props}
                    onClick={onClick}
                    href={paramsData?.as || paramsData?.href}
                    className={classNames('fakeLink', props.className, routerLoader ? 'disableEvents' : '')}
                />
            );
        }
        return (
            <Link
                {...props}
                {...paramsData}
                prefetch={false}
                className={classNames('fakeLink', props.className, routerLoader ? 'disableEvents' : '')}
            />
        );
    }
    return (
        <a
            {...props}
            rel={rel}
            href={href}
            className={classNames('fakeLink', props.className, routerLoader ? 'disableEvents' : '')}
        />
    );
};

export const Img = (props) => <WebImage {...props} {...(props.alt ? {} : { alt: props.src?.src || props.src })} />;

export const Portal = (props) => <ModalPortal {...props}>{props.children}</ModalPortal>;

export const AnimatedDiv = (props) => <AnimatedElement {...props}>{props.children}</AnimatedElement>;

export const Tooltip = (props) => {
    return <TooltipElement {...props}>{props.children}</TooltipElement>;
};

export const Alert = (props) => <AlertElement {...props}>{props.children}</AlertElement>;

export const FieldError = (props) => <FieldErrorElement {...props}>{props.children}</FieldErrorElement>;

export const FieldInfo = (props) => <FieldInfoElement {...props}>{props.children}</FieldInfoElement>;

export const Slider = (props) => <SliderComponent {...props} />;

export const Svg = (props) => <svg {...props}>{props.children}</svg>;
export const Path = (props) => <path {...props}>{props.children}</path>;
export const Defs = (props) => <defs {...props}>{props.children}</defs>;
export const Circle = (props) => <circle {...props}>{props.children}</circle>;
export const G = (props) => <g {...props}>{props.children}</g>;
export const LinearGradient = (props) => <linearGradient {...props}>{props.children}</linearGradient>;
export const RadialGradient = (props) => <radialGradient {...props}>{props.children}</radialGradient>;
export const Mask = (props) => <mask {...props}>{props.children}</mask>;
export const Stop = (props) => <stop {...props}>{props.children}</stop>;
export const Rect = (props) => <rect {...props}>{props.children}</rect>;

export const getDimensions = (withoutZoom) => {
    if (!window) return {};
    let width = window.innerWidth;
    let height = window.innerHeight;

    /* TODO ZOOM */
    if (project.zooming && !withoutZoom) {
        const zoom = Number(window.getComputedStyle(document.querySelector('html'), null).zoom);
        if (zoom === zoom && zoom < 1 && width < 1650 && width >= 1300) {
            height = height / zoom;
            width = width / zoom;
        }
    }
    /* TODO ZOOM */
    return { width, height };
};
export const eventDimension = (func, remove) =>
    remove ? window.removeEventListener('resize', func) : window.addEventListener('resize', func);
